import React, { useCallback, useEffect, useState } from "react";
import { BsCarFrontFill, BsBookmarksFill, BsArrowRepeat } from "react-icons/bs";
import { FaTools, FaUsers } from "react-icons/fa";
import { MdHub, MdTwoWheeler } from "react-icons/md";
import { RiMoneyRupeeCircleFill } from "react-icons/ri";
import Handler from "../Helpers/Handler";
import { Skeleton } from "../Components/Needed";
import { formatDateTime } from "../Helpers/Utils";
import RevenueChart from "./DashboardWidgets/RevenueChart";
import StageFunnel from "./DashboardWidgets/StageFunnel";
import RecentBookings from "./DashboardWidgets/RecentBookings";
import TaskQueue from "./DashboardWidgets/TaskQueue";
import { CustomerPanel, StaffPanel, FleetPanel } from "./DashboardWidgets/SummaryPanels";
import AlertsBanner from "./DashboardWidgets/AlertsBanner";

const CARDS = [
    { id: "cars", label: "Cars", icon: <BsCarFrontFill />, color: "primary" },
    { id: "bikes", label: "Bikes", icon: <MdTwoWheeler />, color: "info" },
    { id: "carbookings", label: "Bookings", icon: <BsBookmarksFill />, color: "success" },
    { id: "customers", label: "Customers", icon: <FaUsers />, color: "warning" },
    { id: "hubs", label: "Hubs", icon: <MdHub />, color: "primary" },
    { id: "services", label: "Services", icon: <FaTools />, color: "info" },
    { id: "payments", label: "Payments", icon: <RiMoneyRupeeCircleFill />, color: "success" },
    { id: "staffs", label: "Staff", icon: <FaUsers />, color: "warning" },
];

// Test mode: the test dashboard counts only test bookings, payments and customers (Razorpay test account).
const TEST_CARDS = ["carbookings", "customers", "payments"];
const MODES = [
    { key: "live", label: "Live" },
    { key: "test", label: "Test" },
];

const PERIODS = [
    { key: "day", label: "Today" },
    { key: "month", label: "This month" },
    { key: "year", label: "This year" },
    { key: "total", label: "All time" },
];

const Dashboard = () => {
    const [period, setPeriod] = useState("total");
    const [mode, setMode] = useState("live");
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [lastLoadedAt, setLastLoadedAt] = useState(null);

    const fetchOverview = useCallback(async () => {
        setLoading(true);
        setError("");
        const response = await Handler({
            method: "GET",
            url: `/admin/dashboard/overview?period=${period}${mode === "test" ? "&mode=test" : ""}`,
        });
        if (response?.success) {
            setOverview(response?.data?.data || {});
            setLastLoadedAt(new Date());
        } else {
            setOverview(null);
            setError(response?.data?.message || "Could not load the dashboard");
        }
        setLoading(false);
    }, [period, mode]);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    const counters = overview?.counters || {};
    const isTest = mode === "test";
    // An older server ignores ?mode=test and sends live figures; never show those as test.
    const testUnsupported = isTest && overview && overview.mode !== "test";
    const cards = isTest ? CARDS.filter((card) => TEST_CARDS.includes(card.id)) : CARDS;
    const switchMode = (next) => {
        if (next === mode) return;
        setOverview(null);
        setMode(next);
    };

    return (
        <div className="dashboard-wrapper">
            <div className="container-fluid">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                    <div>
                        <h2 className="fw-bold text-dark mb-1">
                            {isTest ? "Test dashboard" : "Dashboard Overview"}
                            {isTest ? <span className="badge bg-warning text-dark ms-2 fs-6 align-middle">TEST</span> : null}
                        </h2>
                        <p className="text-muted mb-0">
                            Welcome back. Here's your platform at a glance.
                            {lastLoadedAt ? <span className="ms-2">Updated {formatDateTime(lastLoadedAt)}</span> : null}
                        </p>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="btn-group" role="group" aria-label="Live or test data">
                            {MODES.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`btn btn-sm ${mode === item.key ? (item.key === "test" ? "btn-warning" : "btn-dark") : "btn-light"}`}
                                    onClick={() => switchMode(item.key)}
                                    disabled={loading}
                                    aria-pressed={mode === item.key}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        <div className="btn-group" role="group" aria-label="Counter period">
                            {PERIODS.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`btn btn-sm ${period === item.key ? "btn-primary" : "btn-light"}`}
                                    onClick={() => setPeriod(item.key)}
                                    disabled={loading}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        <button type="button" className="btn btn-sm btn-light" onClick={fetchOverview} disabled={loading} aria-label="Refresh dashboard">
                            <BsArrowRepeat className={loading ? "spin" : ""} />
                        </button>
                    </div>
                </div>

                {error ? (
                    <div className="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
                        <span>{error}</span>
                        <button className="btn btn-sm btn-outline-dark" onClick={fetchOverview}>Retry</button>
                    </div>
                ) : ""}

                {isTest ? (
                    <div className="alert alert-warning d-flex align-items-start gap-2" role="status">
                        <span className="badge bg-warning text-dark mt-1">TEST</span>
                        <span>
                            {testUnsupported
                                ? "This server does not have the test dashboard yet, so the figures below are not test figures. Update the backend and refresh."
                                : "Only test bookings, payments and customers: paid on the Razorpay test account, no real money. The live dashboard leaves all of this out."}
                        </span>
                    </div>
                ) : (
                    <p className="text-muted small mb-3">Test bookings, payments and customers are left out of these figures. Switch to Test to see them.</p>
                )}

                <AlertsBanner alerts={overview?.alerts} loading={loading && !overview} />

                <div className="db-section db-grid-cards">
                    {cards.map((card) => (
                        <div className="card dashboard-card h-100" key={card.id}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className={`icon-box bg-${card.color}-subtle text-${card.color}`}>{card.icon}</div>
                                </div>
                                {loading && !overview ? (
                                    <Skeleton height={32} width="60%" className="mb-2" />
                                ) : (
                                    <h3 className="fw-bold mb-1">{Number(counters?.[card.id] || 0).toLocaleString()}</h3>
                                )}
                                <p className="text-muted mb-0">{card.label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="db-section db-grid-2col">
                    <div className="card dashboard-card">
                        <div className="card-body">
                            <div className="db-panel-title">Revenue trend</div>
                            <div className="db-panel-subtitle">{isTest ? "Test payments minus test refunds, by day (no real money)" : "Captured payments minus refunds, by day"}</div>
                            <RevenueChart data={overview?.revenue} loading={loading && !overview} />
                        </div>
                    </div>
                    <div className="card dashboard-card">
                        <div className="card-body">
                            <div className="db-panel-title">Bookings by stage</div>
                            <div className="db-panel-subtitle">Where every booking currently sits in the lifecycle</div>
                            <StageFunnel byStage={overview?.bookings?.byStage} loading={loading && !overview} />
                        </div>
                    </div>
                </div>

                <div className="db-section db-grid-2col">
                    <div className="card dashboard-card">
                        <div className="card-body">
                            <div className="db-panel-title">Recent bookings</div>
                            <div className="db-panel-subtitle">The latest bookings created, across every stage</div>
                            <RecentBookings list={overview?.bookings?.recent} loading={loading && !overview} />
                        </div>
                    </div>
                    <div className="card dashboard-card">
                        <div className="card-body">
                            <div className="db-panel-title">Delivery &amp; pickup queue</div>
                            <div className="db-panel-subtitle">Open handover tasks, soonest first</div>
                            <TaskQueue tasks={overview?.tasks} loading={loading && !overview} />
                        </div>
                    </div>
                </div>

                <div className="db-section db-grid-3col">
                    <div className="card dashboard-card">
                        <div className="card-body">
                            <div className="db-panel-title">Customer verification</div>
                            <div className="db-panel-subtitle">{isTest ? "KYC status of test-mode customers" : "KYC status across the customer base"}</div>
                            <CustomerPanel data={overview?.customers} loading={loading && !overview} />
                        </div>
                    </div>
                    {/* Staff and fleet have no test data: hidden on the test dashboard. */}
                    {!isTest && (
                        <div className="card dashboard-card">
                            <div className="card-body">
                                <div className="db-panel-title">Staff</div>
                                <div className="db-panel-subtitle">Employment status and hub distribution</div>
                                <StaffPanel data={overview?.staff} loading={loading && !overview} />
                            </div>
                        </div>
                    )}
                    {!isTest && (
                        <div className="card dashboard-card">
                            <div className="card-body">
                                <div className="db-panel-title">Fleet</div>
                                <div className="db-panel-subtitle">Car &amp; bike verification status</div>
                                <FleetPanel data={overview?.fleet} loading={loading && !overview} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
